import { toast } from "sonner"
import {
  IP_RESTRICTED_PATH,
  isBeforeSignInPath,
  isIpNotAllowedError,
  isLeavingForIpRestriction,
  markLeavingForIpRestriction,
  saveIpRestrictedMessage,
} from "@/features/ip-restriction"

export type ApiError = {
  status: number
  statusText: string
  error?: string
  type?: string
  title?: string
  detail?: string
  message?: string
}

// better-auth（/api/auth/*）のエラーは英語の文言のため、認証の画面では理由として添えない
export const getErrorMessage = (error: unknown, fallback?: string): string => {
  const { detail, error: errorText, title, message } = (error ?? {}) as Partial<ApiError>
  // be の defaultHook が返す入力チェックの固定文言は、画面向けの日本語に置き換える
  if (detail === "Validation failed") {
    return "入力内容に誤りがあります。"
  }
  return detail || errorText || title || message || fallback || "不明なエラーが発生しました"
}

// instance 側で表示済み・表示不要のエラーか（共通のトーストを出さない）
export const isErrorToastHandled = (error: unknown): boolean => {
  const { status, type, name } = (error ?? {}) as { status?: number; type?: string; name?: string }
  return (
    status === 401 ||
    type === "/unauthorized" ||
    name === "AbortError" ||
    isIpNotAllowedError(error) ||
    isLeavingForIpRestriction()
  )
}

const maskSensitiveRequestBody = (body?: string | FormData) => {
  if (!body || body instanceof FormData) {
    return body
  }

  try {
    const data = JSON.parse(body)
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return body
    }

    return JSON.stringify({
      ...data,
      password: data.password ? "***" : data.password,
      currentPassword: data.currentPassword ? "***" : data.currentPassword,
      newPassword: data.newPassword ? "***" : data.newPassword,
      token: data.token ? "***" : data.token,
    })
  } catch {
    return body
  }
}

export const customInstance = async <T>(
  url: string,
  {
    method,
    params,
    body,
    signal,
    headers: customHeaders,
  }: {
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
    params?: never
    body?: string | FormData
    responseType?: string
    signal?: AbortSignal
    // eslint-disable-next-line no-undef
    headers?: RequestInit["headers"]
  }
): Promise<T> => {
  // const targetUrl = new URL(url, API_BASE_URL)
  const targetUrl = new URL(url, import.meta.env.VITE_API_URL || "http://localhost")

  // console.log('request date', new Date().toString())
  // console.log('request isServer', isServer)
  console.groupCollapsed(`🔄 ${method}: ${targetUrl.pathname}`)
  console.log("URL:", targetUrl.toString())
  console.log("Data:", maskSensitiveRequestBody(body))
  console.log("Params:", params)
  console.groupEnd()

  /**
   * header設定
   */
  const headers = new Headers({
    "Content-Type": "application/json",
    Accept: "application/json",
    // 'Accept-Language': i18n.language,
    ...customHeaders,
  })

  // Content-Type が multipart/form-data の場合は削除
  // 手動でつけると boundary がなくなり、PHP側でエラーになる
  const contentType = headers.get("Content-Type")
  if (contentType?.startsWith("multipart/form-data")) {
    headers.delete("Content-Type")
  }

  /**
   * tokenのセット
   * TODO: tokenの取得
   */
  // const token = 'Bearer: token'
  // if (token) {
  //   headers.set('Authorization', `Bearer $token`)
  // }

  // GET以外の場合 /csrf-token からCSRFトークンの取得
  // X-Billsan-CSRF-Tokenに設定
  // if (method !== 'GET') {
  //   try {
  //     const csrfToken = await fetch(`$API_BASE_URL/api-go/csrf-token`).then(
  //       (res) => res.json(),
  //     )
  //     headers.set('x-billsan-csrf-token', get(csrfToken, 'csrf_token', ''))
  //   } catch (error) {
  //     console.error('Failed to fetch CSRF token:', error)
  //     throw error
  //   }
  // }

  /**
   * APIコール
   */
  try {
    const response = await fetch(targetUrl.toString(), {
      method,
      ...(body ? { body: body instanceof FormData ? body : body } : {}),
      headers,
      signal,
      credentials: "include",
    })

    console.groupCollapsed(`✅ Response: ${response.status}${response.statusText}`)
    console.log("Response:", response)
    console.log("Status:", response.status)
    console.log("Headers:", Object.fromEntries(response.headers.entries()))
    console.groupEnd()

    if (!response.ok) {
      const errorData: ApiError = {
        status: response.status,
        statusText: response.statusText,
      }

      try {
        const errorResponse = await response.json()
        if (errorResponse && typeof errorResponse === "object") {
          // エラーレスポンスがオブジェクトの場合はそのまま使用
          Object.assign(errorData, errorResponse)
        }
      } catch (e) {
        console.warn("Failed to parse error response as JSON:", e)
      }

      // IP アドレス制限による拒否は、サーバー側でセッションも失効済みのため通常の 403 と分ける
      if (isIpNotAllowedError(errorData)) {
        const message = getErrorMessage(
          errorData,
          "許可されていない IP アドレスからのアクセスです。"
        )
        const { pathname } = window.location
        if (isBeforeSignInPath(pathname)) {
          toast.error(message)
        } else if (pathname !== IP_RESTRICTED_PATH) {
          markLeavingForIpRestriction()
          saveIpRestrictedMessage(message)
          window.location.replace(IP_RESTRICTED_PATH)
        }
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 専用ページへの遷移中は、失効済みセッションによる 401 などで理由を上書きしない。
      if (isLeavingForIpRestriction()) {
        const error = new Error(getErrorMessage(errorData))
        Object.assign(error, errorData)
        throw error
      }

      // ログイン失敗の 401 はセッション切れではないので、専用の文言を出して遷移しない
      if (response.status === 401 && url === "/api/auth/sign-in/email") {
        const message = "メールアドレスまたはパスワードが正しくありません。"
        toast.error(message)
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 401エラーの処理
      if (response.status === 401 || errorData.type === "/unauthorized") {
        const message = getErrorMessage(errorData, "セッションの有効期限が切れました。")
        toast.error(`${message} 再度ログインしてください。`)

        // 少し遅延を入れてからリダイレクト（トーストが表示されるため）
        setTimeout(() => {
          if (window.location.pathname.startsWith("/signin")) {
            return
          }
          const currentPath = window.location.pathname + window.location.search
          window.location.href = `/signin?redirect=${encodeURIComponent(currentPath)}`
        }, 1500)

        // エラーをスローして処理を中断
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 403エラーの処理（表示は画面の onError か共通のトーストが担当する）
      if (response.status === 403 || errorData.type === "/forbidden") {
        const message = getErrorMessage(errorData, "この操作を実行する権限がありません。")
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 404エラーの処理
      if (response.status === 404 || errorData.type === "/not-found") {
        const message = getErrorMessage(errorData, "リソースが見つかりません。")
        // 404は画面側で処理することが多いのでtoastは出さない
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 500番台エラーの処理
      if (response.status >= 500) {
        const message = getErrorMessage(errorData, "サーバーエラーが発生しました。")
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // その他のエラー
      const message = getErrorMessage(errorData)
      const error = new Error(message)
      Object.assign(error, errorData)
      throw error
    }

    // 204 No Content の場合は空のレスポンスを返す
    if (response.status === 204) {
      return {} as T
    }

    const json = await response.json()

    console.groupCollapsed(`📦 Response Data: ${response.status}${response.statusText}`)
    console.log("Data:", json)
    console.groupEnd()

    return json
  } catch (error) {
    console.error("API call error:", error)

    // abortされた場合はそのままエラーを投げる
    if (error instanceof Error && error.name === "AbortError") {
      throw error
    }

    // fetch 自体の失敗（サーバーにつながらない等）
    if (error instanceof TypeError) {
      throw new Error("通信に失敗しました。ネットワーク接続を確認してください。")
    }

    if (error instanceof Error) {
      throw error
    }

    // toast.error('API call failed: Unknown error')
    throw new Error("API call failed: Unknown error")
  }
}

export default customInstance
